import React from "react";
import { useNavigate, Link, Navigate, useLocation } from "react-router-dom";
import {
	Button,
	FormGroup,
	Form,
	Container,
	Row,
	Col,
} from "react-bootstrap";
import { Formik } from "formik";
import { ToastContainer, toast } from "react-toastify";
import { useAuth } from "../../AuthUser";
import API from "../../api";
import { Helmet } from "react-helmet";
import Cookies from "js-cookie";
const currentUserCookieName = "__mygolfdna_current_user__";

function useQuery() {
	const { search } = useLocation();
	return React.useMemo(() => new URLSearchParams(search), [search]);
}

const Signin = () => {
	const { user, setUser } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const query = useQuery();

	// Full "from" (pathname + search + hash) if a protected route did:
	// navigate("/signin", { state: { from: location } })
	const fromLocation = location.state?.from;

	// Helper: compute destination, preserving all current query params.
	const computeDestination = (account = user) => {
		// If ?r=... and they can submit lesson, go to /upload-videos with ALL current query params
		if (query.get("r") && account?.canSubmitLesson) {
			return { pathname: "/upload-videos", search: location.search };
		}
		// Your existing consult flow (also keep params just in case)
		if (account?.canSubmitConsult && !account?.isConsultCompleted) {
			return { pathname: "/wizard/about", search: location.search };
		}
		// Otherwise return them to where they came from (includes its own search/hash),
		// or default to /videos while preserving current params.
		if (fromLocation && fromLocation.pathname) {
			return {
				pathname: fromLocation.pathname,
				search: fromLocation.search || "",
				hash: fromLocation.hash || "",
			};
		}
		return { pathname: "/videos", search: location.search };
	};

	// If already logged in, redirect immediately via same rules.
	if (user) {
		const dest = computeDestination(user);
		return <Navigate to={dest} replace />;
	}

	const notify = (message) =>
		toast.error(message, {
			position: toast.POSITION.TOP_RIGHT,
		});

	return (
		<Formik
			initialValues={{ email: "", password: "" }}
			validate={(values) => {
				const errors = {};
				if (!values.email) {
					errors.email = "Required";
				} else if (
					!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
				) {
					errors.email = "Invalid email address";
				}
				if (!values.password) {
					errors.password = "Required";
				}
				return errors;
			}}
			onSubmit={(values, { setSubmitting, setErrors }) => {
				setTimeout(() => {
					// Keep your API call exactly the same (still sends ?r= to backend)
					API.post(`auth/signin?r=${query.get("r")}`, {
						email: values.email,
						password: values.password,
					})
						.then((res) => {
							setUser(res.data);

							// Preserve your original branching, but now *also* preserve all query params
							if (query.get("r") && res.data.canSubmitLesson) {
								navigate({ pathname: "/upload-videos", search: location.search }, { replace: true });
								setSubmitting(false);
								return;
							}
							if (res.data.canSubmitConsult && !res.data.isConsultCompleted) {
								navigate({ pathname: "/wizard/about", search: location.search }, { replace: true });
								setSubmitting(false);
								return;
							}

							// Fall back to where they came from, or /videos, carrying query params as described above
							navigate(computeDestination(res.data), { replace: true });
							setSubmitting(false);
						})
						.catch((error) => {
							setSubmitting(false);
							if (error.response) {
								notify(error.response.data.message);
							}
							if (
								error.response &&
								error.response.data.errors &&
								error.response.data.errors.length
							) {
								const result = error.response.data.errors.reduce(function (
										map,
										obj
									) {
										map[obj.property] = obj.message;
										return map;
									},
									{});
								setErrors(result);
							}
						});
				}, 400);
			}}
		>
			{({
				  values,
				  errors,
				  touched,
				  handleChange,
				  handleSubmit,
				  isSubmitting,
			  }) => (
				<>
					<Helmet>
						<title>Signin | MyGolfDNA.com</title>
						<meta name="description" content="signin to your account" />
					</Helmet>
					<Container>
						<Row>
							<Col sm={12} md={{ span: 6, offset: 3 }} lg={{ span: 6, offset: 3 }} xl={{ span: 4, offset: 4 }}>
								<h2 className="text-white text-center mt-5">Sign in</h2>
								<Form noValidate className="mt-5 form-group" onSubmit={handleSubmit}>
									<Form.Group className="form-floating mb-3">
										<Form.Control
											className="bg-dark border-light border-opacity-25 text-light"
											placeholder="Email address"
											type="email"
											name="email"
											id="email"
											onChange={handleChange}
											value={values.email}
											isInvalid={errors.email && touched.email}
											isValid={!errors.email && touched.email}
										/>
										<Form.Label className="text-white" htmlFor="email">Email address</Form.Label>
										<div className="invalid-feedback">{errors.email}</div>
									</Form.Group>
									<Form.Group className="form-floating mb-3">
										<Form.Control
											className="bg-dark border-light border-opacity-25 text-white"
											placeholder="Password"
											type="password"
											name="password"
											id="password"
											onChange={handleChange}
											value={values.password}
											isInvalid={errors.password && touched.password}
										/>
										<Form.Label className="text-white" htmlFor="password">Your password</Form.Label>
										<div className="invalid-feedback">{errors.password}</div>
									</Form.Group>
									<FormGroup className="mt-5">
										<div className="d-grid gap-4">
											<Button variant="secondary" size="lg" type="submit" disabled={isSubmitting}>
												{isSubmitting ? (
													<>
                            <span
								className="spinner-border spinner-border-sm"
								role="status"
								aria-hidden={true}
							/>{" "}
													</>
												) : (
													"Sign in"
												)}
											</Button>
											<Button variant="link" as={Link} to="/forgot-password" size="sm" className="text-light text-opacity-75">
												Forgot your password?
											</Button>
											<Button variant="link" as={Link} to="/free" size="sm" className="text-light text-opacity-75">
												Don't have an account? Sign up
											</Button>
										</div>
									</FormGroup>
								</Form>
							</Col>
						</Row>
						<ToastContainer />
					</Container>
				</>
			)}
		</Formik>
	);
};

export default Signin;
